import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { AppConfig } from "../AppConfig";
import { LocalStorageManager } from "../core/storage/LocalStorageManager";
import { appActions } from "../ducks/app";
import {
  AppCoupon,
  AppliedCoupon,
  couponActions,
  CouponType,
} from "../ducks/coupon";
import { MenuInCart } from "../ducks/menu";
import { Store } from "../ducks/store";
import { CouponHook } from "./useCoupon";

/* Sub Logic Hook for applying selected app coupons recursively
 * Hook to apply selected app coupons recursively
 * @param useCoupon - useCoupon hook
 * @returns {Object} - Object containing handleApplySelectedAppCoupons function
 */
export const useApplySelectedAppCoupon = (useCoupon: CouponHook) => {
  const {
    selectedAppCoupons,
    validateCoupon,
    applyCoupon,
    handleInvalidCouponAppToken,
    handleErrorAppFreeCouponMaxLimit,
  } = useCoupon;
  const dispatch = useDispatch();
  const [isApplyingSelectedAppCoupons, setIsApplyingSelectedAppCoupons] =
    useState(false);

  // Skip reset if currently applying to prevent re-triggering during validation error handling
  useEffect(() => {
    if (isApplyingSelectedAppCoupons) return;
    setIsApplyingSelectedAppCoupons(false);
  }, [selectedAppCoupons]);

  const validateSelectedAppIsFreeCoupons = useCallback(
    ({
      store,
      storePickupDate,
      selectedAppCouponsToApply,
      appliedCoupons,
      onOk,
    }: {
      store: Store;
      storePickupDate: string;
      selectedAppCouponsToApply: AppCoupon[];
      appliedCoupons: AppliedCoupon[];
      onOk: () => void;
    }): void => {
      const appToken = LocalStorageManager.Instance.getObject<string>(
        AppConfig.Instance.LocalStorageKey["appToken"],
      );

      // skip check if no app token
      if (!appToken) return onOk();
      const isIncludeFreeCoupons = selectedAppCouponsToApply.some(
        (coupon) => coupon.isFreeCoupon,
      );
      if (!isIncludeFreeCoupons) return onOk();

      // proceed to check if there are enough remaining slots
      dispatch(
        couponActions.getCouponExternals.started({
          app_token: appToken,
          store_id: store.id,
          pickup_date: storePickupDate,
          onSuccess(result: { remainingSlot: number }) {
            const { remainingSlot } = result;
            const total =
              appliedCoupons.filter((coupon) => coupon.isFreeCoupon).length +
              selectedAppCouponsToApply.filter((coupon) => coupon.isFreeCoupon)
                .length;
            if (total > remainingSlot) {
              handleErrorAppFreeCouponMaxLimit(() => {
                dispatch(
                  couponActions.removeSelectedAppCoupon.started({
                    couponCodes: selectedAppCouponsToApply
                      .filter((coupon) => coupon.isFreeCoupon)
                      .map((coupon) => coupon.code),
                  }),
                );
                dispatch(appActions.closeModal());
              });
              return;
            }
            onOk();
          },
          onError() {
            handleInvalidCouponAppToken({
              storeId: store.id,
              pickupDate: storePickupDate,
              storeShortestTimeUntilPickup: store.shortestTimeUntilPickup,
              redirectUrl: window.location.href,
            });
          },
        }),
      );
    },
    [handleInvalidCouponAppToken, handleErrorAppFreeCouponMaxLimit],
  );

  const applyCouponsRecursively = ({
    selectedAppCouponsToApply,
    couponCodes,
    store,
    storePickupDate,
  }: {
    selectedAppCouponsToApply: AppCoupon[];
    couponCodes: string[];
    store: Store;
    storePickupDate: string;
  }) => {
    let couponsToApply = selectedAppCouponsToApply.filter((coupon) =>
      couponCodes.includes(coupon.code),
    );

    // Partition coupons into menu coupons and total coupons
    const menuCouponCodes: string[] = [];
    const totalCouponCodes: string[] = [];

    couponsToApply.forEach((coupon) => {
      if (coupon.menuCode === null) totalCouponCodes.push(coupon.code);
      else menuCouponCodes.push(coupon.code);
    });

    // Helper function to create batches from a list of coupon codes
    const createBatches = (couponCodes: string[]): string[][] => {
      const batches: string[][] = [];
      const countMap = new Map<string, number>();

      // Count occurrences
      couponCodes.forEach((coupon) => {
        countMap.set(coupon, (countMap.get(coupon) || 0) + 1);
      });

      // Create batches - each batch contains one instance of each unique coupon
      const maxOccurrence = Math.max(...Array.from(countMap.values()), 0);
      for (let i = 0; i < maxOccurrence; i++) {
        const batch: string[] = [];
        countMap.forEach((count, coupon) => {
          if (i < count) {
            batch.push(coupon);
          }
        });
        batches.push(batch);
      }

      return batches;
    };

    const menuBatches = createBatches(menuCouponCodes);
    const totalBatches = createBatches(totalCouponCodes);

    // Apply menu batches first, then total batches
    const applyBatches = (
      batches: string[][],
      batchIdx: number,
      onComplete: () => void,
    ) => {
      if (batchIdx >= batches.length) {
        onComplete();
        return;
      }
      applyCoupon(
        store.id,
        storePickupDate,
        store.shortestTimeUntilPickup,
        batches[batchIdx],
        true,
        undefined,
        () => applyBatches(batches, batchIdx + 1, onComplete),
      );
    };

    // Early exit if no batches
    if (menuBatches.length === 0 && totalBatches.length === 0) {
      setIsApplyingSelectedAppCoupons(false);
      return;
    }

    // Apply menu batches first, then total batches
    applyBatches(menuBatches, 0, () => {
      applyBatches(totalBatches, 0, () => {
        setIsApplyingSelectedAppCoupons(false);
      });
    });
  };

  const validateAppMultipleCoupons = useCallback(
    ({
      selectedAppCoupons,
      appliedCoupons,
      menusInCart,
      onDone,
    }: {
      selectedAppCoupons: AppCoupon[];
      appliedCoupons: AppliedCoupon[];
      menusInCart: MenuInCart[];
      onDone: (couponsToApply: AppCoupon[]) => void;
    }) => {
      let couponsToApply = selectedAppCoupons;
      const couponMap = new Map<string, AppCoupon>(
        selectedAppCoupons.map((coupon) => [coupon.code, coupon]),
      );
      // Validate AppMulti menu coupons: if already in cart/applied, show modal and skip adding duplicates
      const duplicateMultiCodes = new Set<string>();
      couponsToApply.forEach((coupon) => {
        const isMenuCoupon = coupon.menuCode !== null;
        const isAppMulti = coupon.kind === CouponType.AppMulti;
        if (!isMenuCoupon || !isAppMulti) return;
        const alreadyApplied =
          (appliedCoupons ?? []).some(
            (c) => c.isAppCoupon && !c.isTotalCoupon && c.code === coupon.code,
          ) || (menusInCart ?? []).some((m) => m.couponCode === coupon.code);
        if (alreadyApplied) {
          duplicateMultiCodes.add(coupon.code);
        }
      });
      if (duplicateMultiCodes.size === 0) {
        onDone(couponsToApply);
        return;
      }

      if (duplicateMultiCodes.size > 0) {
        dispatch(
          appActions.showModal.started({
            title:
              "すでに同一のクーポンがあります。\nカート内で該当のメニューの\n数量を変更してください。",
            subTitle: `${Array.from(duplicateMultiCodes)
              .map((code) => `[${code}]: ${couponMap.get(code)?.name}`)
              .join("\n")}`,
            nextButtonLabel: "閉じる",
            handleNext: () => {
              dispatch(
                couponActions.removeSelectedAppCoupon.started({
                  couponCodes: Array.from(duplicateMultiCodes),
                }),
              );
              dispatch(appActions.closeModal());
              couponsToApply = couponsToApply.filter(
                (coupon) => !duplicateMultiCodes.has(coupon.code),
              );
              onDone(couponsToApply);
            },
          }),
        );
      }
    },
    [],
  );
  const handleApplySelectedAppCoupons = useCallback(
    ({
      store,
      storePickupDate,
      selectedAppCoupons,
      appliedCoupons,
      menusInCart,
    }: {
      store: Store;
      storePickupDate: string;
      selectedAppCoupons: AppCoupon[];
      appliedCoupons?: AppliedCoupon[] | null;
      menusInCart?: MenuInCart[] | null;
    }) => {
      if (isApplyingSelectedAppCoupons) return; // Prevent duplicate application

      validateAppMultipleCoupons({
        selectedAppCoupons,
        appliedCoupons: appliedCoupons ?? [],
        menusInCart: menusInCart ?? [],
        onDone: (selectedAppCouponsToApply) => {
          validateSelectedAppIsFreeCoupons({
            store,
            storePickupDate,
            selectedAppCouponsToApply,
            appliedCoupons: appliedCoupons ?? [],
            onOk: () => {
              setIsApplyingSelectedAppCoupons(true);
              validateCoupon(
                store.id,
                storePickupDate,
                store.shortestTimeUntilPickup,
                (couponCodes) =>
                  applyCouponsRecursively({
                    selectedAppCouponsToApply,
                    couponCodes,
                    store,
                    storePickupDate,
                  }),
                true,
              );
            },
          });
        },
      });
    },
    [isApplyingSelectedAppCoupons, validateSelectedAppIsFreeCoupons],
  );

  return {
    handleApplySelectedAppCoupons,
    isApplyingSelectedAppCoupons,
    setIsApplyingSelectedAppCoupons,
  };
};
